import { format } from 'date-fns';
import { CalendarRangeIcon, XIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Calendar, type CalendarProps } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export type DateRange = { from?: Date; to?: Date };

const TRIGGER_DATE_FORMAT = 'd MMM yyyy';

type DateRangePickerProps = {
  value: DateRange;
  onChange: (next: DateRange) => void;
  /** i18n key of the label shown while no date is selected. */
  placeholder?: string;
  className?: string;
  align?: 'start' | 'center' | 'end';
  /** Extra props forwarded to the underlying range Calendar. */
  calendarProps?: Omit<
    Extract<CalendarProps, { mode: 'range' }>,
    'mode' | 'selected' | 'onSelect'
  >;
};

/**
 * Formats the trigger label for the current range selection.
 *
 * @param params.value - the current range
 * @param params.placeholder - text shown when the range is empty
 * @param params.t - the i18n translate function
 * @returns the label to render inside the trigger button
 */
const formatTriggerLabel = (params: {
  value: DateRange;
  placeholder: string;
  t: (key: string, options?: Record<string, unknown>) => string;
}): string => {
  const { value, placeholder, t } = params;
  if (value.from && value.to) {
    return `${format(value.from, TRIGGER_DATE_FORMAT)} – ${format(value.to, TRIGGER_DATE_FORMAT)}`;
  }
  if (value.from) {
    return t('date-range-picker.from', {
      date: format(value.from, TRIGGER_DATE_FORMAT),
    });
  }
  if (value.to) {
    return t('date-range-picker.to', {
      date: format(value.to, TRIGGER_DATE_FORMAT),
    });
  }
  return placeholder;
};

/**
 * Controlled date-range picker: outline trigger with a formatted range label,
 * inline clear button and a 2-month range calendar. Ported from the
 * omecare-mockups pattern.
 *
 * @param props.value - the current range
 * @param props.onChange - called with the next range (empty object on clear)
 * @param props.placeholder - i18n key shown while empty
 * @param props.className - extra classes merged on the trigger button
 * @param props.align - popover alignment
 * @param props.calendarProps - extra props forwarded to the range Calendar
 * @returns the date-range picker
 */
export const DateRangePicker = ({
  value,
  onChange,
  placeholder = 'date-range-picker.placeholder',
  className,
  align = 'start',
  calendarProps,
}: DateRangePickerProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const hasValue = !!value.from || !!value.to;

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
    >
      <div className={cn('relative inline-flex', className)}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              'w-full min-w-[220px] justify-between font-normal',
              hasValue && 'pr-9',
              !hasValue && 'text-muted-foreground',
            )}
          >
            <span className="flex items-center gap-2 truncate">
              <CalendarRangeIcon
                className="size-4 text-muted-foreground"
                aria-hidden
              />
              {formatTriggerLabel({ value, placeholder: t(placeholder), t })}
            </span>
          </Button>
        </PopoverTrigger>
        {hasValue && (
          <button
            type="button"
            onClick={() => onChange({})}
            aria-label={t('date-range-picker.clear')}
            className="absolute top-1/2 right-2 inline-flex size-4 -translate-y-1/2 items-center justify-center rounded-sm hover:bg-muted"
          >
            <XIcon
              className="size-3"
              aria-hidden
            />
          </button>
        )}
      </div>
      <PopoverContent
        align={align}
        className="w-auto p-0"
      >
        <Calendar
          mode="range"
          selected={value.from ? { from: value.from, to: value.to } : undefined}
          onSelect={(range) => onChange({ from: range?.from, to: range?.to })}
          numberOfMonths={2}
          autoFocus
          {...calendarProps}
        />
      </PopoverContent>
    </Popover>
  );
};
